import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { validateContent, sanitizeContent } from '@/utils/sanitizeContent';
import { invalidateLibraryCache } from '@/hooks/useLibraryContent';
import { Json } from '@/integrations/supabase/types';

interface ScriptInsights {
  perplexityInsights: Json | null;
  altHooks: Json | null;
  textOverlays: Json | null;
  improvementsApplied: Json | null;
}

interface ScriptData {
  id: string;
  title: string;
  originalContent: string;
  editedContent: string | null;
  table: 'content_ideas' | 'user_generated_content';
  insights: ScriptInsights;
  isFavorited: boolean;
}

interface UseScriptEditorReturn {
  scriptData: ScriptData | null;
  editedContent: string;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  hasChanges: boolean;
  setEditedContent: (content: string) => void;
  saveChanges: () => Promise<boolean>;
  restoreOriginal: () => Promise<boolean>;
  loadScript: (id: string, table: 'content_ideas' | 'user_generated_content') => Promise<void>;
}

const AUTOSAVE_DELAY = 3000; // 3 seconds

export const useScriptEditor = (): UseScriptEditorReturn => {
  const [scriptData, setScriptData] = useState<ScriptData | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  const { toast } = useToast();
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Extract script content from potentially JSON-wrapped content
  const extractScriptContent = useCallback((content: string | null): string => {
    if (!content) return '';
    
    // Try to parse as JSON first (before any unescaping that could corrupt valid JSON)
    try {
      const parsed = JSON.parse(content);
      if (parsed && typeof parsed === 'object' && 'script' in parsed) {
        return parsed.script || '';
      }
    } catch {
      // Not valid JSON as-is, try after unescaping
    }

    // Convert <br> tags and unescape literal \n sequences, then retry
    const unescaped = content
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/\\n\\n/g, '\n\n')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\\\/g, '\\');

    try {
      const parsed = JSON.parse(unescaped);
      if (parsed && typeof parsed === 'object' && 'script' in parsed) {
        return parsed.script || '';
      }
    } catch {
      // Not JSON at all, return unescaped content as fallback
    }
    
    return unescaped;
  }, []);

  // Convert legacy <br> tags to newlines for textarea display
  const normalizeBrTags = (text: string): string => text.replace(/<br\s*\/?>/gi, '\n');

  // Get the content to display (edited or original)
  const getDisplayContent = useCallback((data: { content?: string | null; description?: string | null; edited_content?: string | null }): string => {
    if (data.edited_content) return normalizeBrTags(data.edited_content);
    return extractScriptContent(data.content) || data.description || '';
  }, [extractScriptContent]);

  // Load script data from database
  const loadScript = useCallback(async (id: string, table: 'content_ideas' | 'user_generated_content') => {
    setIsLoading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error('Nie jesteś zalogowany');
      }

      const { data, error } = await supabase
        .from(table)
        .select('id, title, content, description, edited_content, perplexity_insights, alt_hooks, text_overlays, improvements_applied, is_favorited')
        .eq('id', id)
        .eq('user_id', userData.user.id)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Nie znaleziono scenariusza');

      const originalContent = extractScriptContent(data.content) || data.description || '';
      const script: ScriptData = {
        id: data.id,
        title: data.title,
        originalContent,
        editedContent: data.edited_content,
        table,
        insights: {
          perplexityInsights: data.perplexity_insights ?? null,
          altHooks: data.alt_hooks ?? null,
          textOverlays: data.text_overlays ?? null,
          improvementsApplied: data.improvements_applied ?? null,
        },
        isFavorited: data.is_favorited ?? false,
      };

      setScriptData(script);
      setEditedContent(data.edited_content || originalContent);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading script:', error);
      toast({
        title: 'Błąd',
        description: 'Nie udało się załadować scenariusza',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Save changes to database
  const saveChanges = useCallback(async (): Promise<boolean> => {
    if (!scriptData) return false;

    // Validate content
    const validation = validateContent(editedContent);
    if (!validation.isValid) {
      toast({
        title: 'Błąd walidacji',
        description: validation.error,
        variant: 'destructive',
      });
      return false;
    }

    // Sanitize content
    const sanitization = sanitizeContent(editedContent);
    if (!sanitization.isValid) {
      toast({
        title: 'Błąd',
        description: sanitization.error,
        variant: 'destructive',
      });
      return false;
    }

    const contentToSave = sanitization.sanitizedContent;

    setIsSaving(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error('Nie jesteś zalogowany');
      }

      const { error } = await supabase
        .from(scriptData.table)
        .update({ edited_content: contentToSave })
        .eq('id', scriptData.id)
        .eq('user_id', userData.user.id);

      if (error) throw error;

      setLastSaved(new Date());
      setHasChanges(false);
      setScriptData(prev => prev ? { ...prev, editedContent: contentToSave } : null);

      // Invalidate library cache so fresh data is fetched on navigation
      invalidateLibraryCache();

      toast({
        title: 'Zapisano',
        description: 'Zmiany zostały zapisane',
      });

      return true;
    } catch (error) {
      console.error('Error saving changes:', error);
      toast({
        title: 'Błąd',
        description: 'Nie udało się zapisać zmian. Spróbuj ponownie.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [scriptData, editedContent, toast]);

  // Restore original content
  const restoreOriginal = useCallback(async (): Promise<boolean> => {
    if (!scriptData) return false;

    setIsSaving(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error('Nie jesteś zalogowany');
      }

      const { error } = await supabase
        .from(scriptData.table)
        .update({ edited_content: null })
        .eq('id', scriptData.id)
        .eq('user_id', userData.user.id);

      if (error) throw error;

      setEditedContent(scriptData.originalContent);
      setScriptData(prev => prev ? { ...prev, editedContent: null } : null);
      setHasChanges(false);
      setLastSaved(null);

      toast({
        title: 'Przywrócono',
        description: 'Oryginalna wersja została przywrócona',
      });

      return true;
    } catch (error) {
      console.error('Error restoring original:', error);
      toast({
        title: 'Błąd',
        description: 'Nie udało się przywrócić oryginału',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [scriptData, toast]);

  // Handle content changes with autosave
  const handleSetEditedContent = useCallback((content: string) => {
    setEditedContent(content);
    setHasChanges(true);

    // Clear existing autosave timer
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    // Set new autosave timer
    autosaveTimerRef.current = setTimeout(() => {
      // Only autosave if there are actual changes
      if (scriptData && content !== scriptData.originalContent && content !== scriptData.editedContent) {
        saveChanges();
      }
    }, AUTOSAVE_DELAY);
  }, [scriptData, saveChanges]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  // Keyboard shortcut for save (Cmd/Ctrl + S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (hasChanges) {
          saveChanges();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasChanges, saveChanges]);

  return {
    scriptData,
    editedContent,
    isLoading,
    isSaving,
    lastSaved,
    hasChanges,
    setEditedContent: handleSetEditedContent,
    saveChanges,
    restoreOriginal,
    loadScript,
  };
};
